import { useState, useEffect, useRef } from 'react';
import PhoneShell from '../components/shared/PhoneShell';
import SportFilter from '../components/shared/SportFilter';
import MatchCard from '../components/shared/MatchCard';
import { maskStyle } from '../utils/icons';
import { useLang } from '../i18n';

const API      = process.env.REACT_APP_API_BASE_URL;
const POLL_MS  = 30_000;

/* ── Helpers ────────────────────────────────── */
function buildDates() {
  const DAYS = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i);
    return { dow: i === 0 ? 'Auj.' : DAYS[d.getDay()], dom: String(d.getDate()).padStart(2, '0') };
  });
}

function mapOdds(oddsData) {
  if (!oddsData?.match_winner?.length) return [{ locked: true }, { locked: true }, { locked: true }];
  const find = (lbl) => oddsData.match_winner.find((o) => o.label === lbl);
  return ['Home', 'Draw', 'Away'].map((lbl) => {
    const o = find(lbl);
    return o ? { v: parseFloat(o.odd).toFixed(2) } : { locked: true };
  });
}

function getStatus(status, t) {
  if (!status) return 'LIVE';
  const { short, elapsed } = status;
  if (short === 'HT') return t('calendar.halfTime');
  if (short === 'FT' || short === 'AET') return t('calendar.finished');
  if (['1H', '2H', 'ET', 'P'].includes(short)) return `${elapsed || 0}'`;
  return elapsed ? `${elapsed}'` : 'LIVE';
}

function groupByLeague(fixtures, t) {
  const map = new Map();
  for (const f of fixtures) {
    const key = f.league?.id || 'other';
    if (!map.has(key)) {
      map.set(key, { name: f.league?.name || 'Football', country: f.league?.country || '', logo: f.league?.logo, matches: [] });
    }
    map.get(key).matches.push({
      home:      f.teams?.home?.name || '—',
      away:      f.teams?.away?.name || '—',
      live:      true,
      homeScore: f.goals?.home ?? null,
      awayScore: f.goals?.away ?? null,
      odds:      mapOdds(f.odds),
      status:    getStatus(f.fixture?.status, t),
      href:      `/match/${f.fixture?.id}`,
    });
  }
  return Array.from(map.values());
}

/* ── CSS ────────────────────────────────────── */
const css = `
  .cal-dates { display: flex; gap: 8px; padding: 10px 16px 6px; overflow-x: auto; scrollbar-width: none; }
  .cal-dates::-webkit-scrollbar { display: none; }
  .cal-date { flex: none; width: 52px; height: 62px; border-radius: 10px; background: #1d1c3c; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px; border: none; cursor: pointer; transition: background .15s; }
  .cal-date .dow { font-size: 11px; color: #757490; font-weight: 600; }
  .cal-date .dom { font-size: 18px; color: #fff; font-weight: 700; }
  .cal-date.is-active { background: linear-gradient(160deg, #8187f6, #76e1ff); }
  .cal-date.is-active .dow, .cal-date.is-active .dom { color: #fff; }

  .cal-group-title { display: flex; align-items: center; gap: 8px; padding: 16px 16px 8px; }
  .cal-group-title .ic { width: 13px; height: 13px; background: #76e1ff; flex-shrink: 0; }
  .cal-group-title h3 { font-size: 13px; font-weight: 700; color: #fff; flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .cal-group-title .cnt { font-size: 12px; color: #757490; flex-shrink: 0; }

  .cal-list { display: flex; flex-direction: column; gap: 8px; padding: 0 8px; }

  .cal-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px; padding: 64px 24px; color: #757490; font-size: 14px; text-align: center; }
  .cal-empty svg { opacity: .35; }

  .cal-pulse { display: flex; justify-content: center; padding: 64px 0; }
  .cal-pulse-dot { width: 8px; height: 8px; border-radius: 50%; background: #76e1ff; animation: calPulse 1.2s ease-in-out infinite; }
  .cal-pulse-dot:nth-child(2) { animation-delay: .2s; }
  .cal-pulse-dot:nth-child(3) { animation-delay: .4s; }
  @keyframes calPulse { 0%,80%,100%{opacity:.2;transform:scale(.8)} 40%{opacity:1;transform:scale(1)} }

  .cal-refresh-hint { display: flex; align-items: center; gap: 6px; font-size: 11px; color: #46455e; padding: 6px 16px; }
  .cal-refresh-hint svg { animation: calSpin 2s linear infinite; }
  @keyframes calSpin { to { transform: rotate(360deg); } }
  .cal-live-badge { display: flex; align-items: center; gap: 4px; font-size: 10px; font-weight: 800; color: #ff5d6c; text-transform: uppercase; letter-spacing: .04em; margin-left: 4px; }
  .cal-live-dot { width: 6px; height: 6px; border-radius: 50%; background: #ff5d6c; animation: livePulse 1s ease-in-out infinite; }
  @keyframes livePulse { 0%,100%{opacity:1} 50%{opacity:.3} }
`;

const DATES = buildDates();

export default function LiveCalendar() {
  const { t } = useLang();
  const [active, setActive]   = useState(0);
  const [groups, setGroups]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const timerRef = useRef(null);

  const fetchLive = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res  = await fetch(`${API}/api-bet/football/live.php`);
      const data = await res.json();
      if (data.success && Array.isArray(data.fixtures)) {
        setGroups(groupByLeague(data.fixtures, t));
      } else {
        setGroups([]);
      }
      setLastRefresh(new Date());
    } catch {
      if (!silent) setGroups([]);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchLive();
    timerRef.current = setInterval(() => fetchLive(true), POLL_MS);
    return () => clearInterval(timerRef.current);
  }, []);

  const totalMatches = groups.reduce((s, g) => s + g.matches.length, 0);

  return (
    <PhoneShell activeTab="sports">
      <style>{css}</style>

      {/* Date strip */}
      <div className="cal-dates">
        {DATES.map((d, i) => (
          <button key={i} className={'cal-date' + (i === active ? ' is-active' : '')} onClick={() => setActive(i)}>
            <span className="dow">{d.dow}</span>
            <span className="dom">{d.dom}</span>
          </button>
        ))}
      </div>

      <SportFilter />

      {/* Refresh hint */}
      {lastRefresh && !loading && (
        <div className="cal-refresh-hint">
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M9 5a4 4 0 11-4-4" stroke="#46455e" strokeWidth="1.2" strokeLinecap="round"/>
            <path d="M5 1l1.5 1.5L5 4" stroke="#46455e" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {t('calendar.autoRefresh')}
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="cal-pulse">
          <div style={{ display: 'flex', gap: 6 }}>
            <div className="cal-pulse-dot" />
            <div className="cal-pulse-dot" />
            <div className="cal-pulse-dot" />
          </div>
        </div>
      ) : active === 0 && totalMatches > 0 ? (
        groups.map((g, gi) => (
          <section key={gi}>
            <div className="cal-group-title">
              <span className="ic" style={maskStyle('ic-calendar')} />
              <h3>{g.country ? `${g.country} — ${g.name}` : g.name}</h3>
              <div className="cal-live-badge">
                <span className="cal-live-dot" />
                LIVE
              </div>
              <span className="cnt">{g.matches.length} match{g.matches.length > 1 ? 's' : ''}</span>
            </div>
            <div className="cal-list">
              {g.matches.map((m, j) => <MatchCard key={j} match={m} />)}
            </div>
          </section>
        ))
      ) : (
        <div className="cal-empty">
          <svg width="56" height="56" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="4" width="18" height="17" rx="3" stroke="#9b9ab0" strokeWidth="1.3"/>
            <path d="M8 2v4M16 2v4M3 9h18" stroke="#9b9ab0" strokeWidth="1.3" strokeLinecap="round"/>
            <path d="M8 13h4M8 16h3" stroke="#9b9ab0" strokeWidth="1.2" strokeLinecap="round"/>
          </svg>
          {active === 0
            ? t('calendar.noLive')
            : t('calendar.noScheduled')}
        </div>
      )}

      <div style={{ height: 20 }} />
    </PhoneShell>
  );
}
